import React, { useState } from 'react';
import axios from 'axios';
import { X, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConsultationModal = ({ isOpen, onClose, serviceType = 'General' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    company: '',
    requirement: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Send to corporate training endpoint
      await axios.post(`${API_URL}/api/corporate-training`, {
        organizationName: formData.company || 'Individual',
        contactPerson: formData.name,
        email: formData.email,
        trainingRequirement: `[${serviceType}] ${formData.requirement}`,
        participants: formData.message || 'Consultation Request'
      });

      setSuccess(true);
      toast.success('Consultation request submitted successfully!');
      
      // Track form submission for analytics
      if (window.trackFormSubmission) {
        window.trackFormSubmission(`consultation_${serviceType}`);
      }

      setTimeout(() => {
        setSuccess(false);
        setFormData({ name: '', email: '', mobile: '', company: '', requirement: '', message: '' });
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="relative bg-[#111827] border border-[#00E5FF]/30 rounded-2xl shadow-2xl shadow-[#00E5FF]/20 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#00E5FF]/20">
          <div>
            <h2 className="text-2xl font-bold text-white font-['Space_Grotesk']">Request Consultation</h2>
            <p className="text-[#9CA3AF] text-sm mt-1">{serviceType} Services</p>
          </div>
          <button onClick={onClose} className="text-[#9CA3AF] hover:text-white p-2 rounded-lg hover:bg-[#00E5FF]/10 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
            <p className="text-[#9CA3AF]">We'll get back to you within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-1">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0B0F1A] border border-[#00E5FF]/20 rounded-lg text-white placeholder-[#6B7280] focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#9CA3AF] mb-1">Mobile *</label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0B0F1A] border border-[#00E5FF]/20 rounded-lg text-white placeholder-[#6B7280] focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9CA3AF] mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0B0F1A] border border-[#00E5FF]/20 rounded-lg text-white placeholder-[#6B7280] focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
                placeholder="john@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9CA3AF] mb-1">Company/Organization</label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0B0F1A] border border-[#00E5FF]/20 rounded-lg text-white placeholder-[#6B7280] focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
                placeholder="Company Name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9CA3AF] mb-1">Requirement *</label>
              <select
                name="requirement"
                value={formData.requirement}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0B0F1A] border border-[#00E5FF]/20 rounded-lg text-white focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all"
              >
                <option value="">Select your requirement</option>
                {serviceType === 'Corporate Training' && (
                  <>
                    <option value="Corporate IT Training">Corporate IT Training</option>
                    <option value="Professional Programs">Professional Programs</option>
                    <option value="Student Training">Student/Fresher Training</option>
                    <option value="Placement Assistance">Placement Assistance</option>
                    <option value="Corporate Workshops">Corporate Workshops</option>
                  </>
                )}
                {serviceType === 'Recruitment' && (
                  <>
                    <option value="IT Recruitment">IT Recruitment</option>
                    <option value="Non-IT Recruitment">Non-IT Recruitment</option>
                    <option value="Staffing Services">Staffing Services</option>
                    <option value="Campus Hiring">Campus Hiring</option>
                    <option value="Skill-Based Screening">Skill-Based Screening</option>
                  </>
                )}
                {serviceType === 'Software Development' && (
                  <>
                    <option value="Custom Software">Custom Software Development</option>
                    <option value="Web Application">Web Application Development</option>
                    <option value="Mobile App">Mobile App Development</option>
                    <option value="API Development">API Development & Integration</option>
                    <option value="AI Solutions">AI & Automation Solutions</option>
                  </>
                )}
                {serviceType === 'IT Services' && (
                  <>
                    <option value="Cloud Services">Cloud Services (AWS/Azure/GCP)</option>
                    <option value="DevOps">DevOps & Site Reliability</option>
                    <option value="Cyber Security">Cyber Security Services</option>
                    <option value="Infrastructure">Infrastructure Management</option>
                    <option value="IT Consulting">IT Consulting</option>
                  </>
                )}
                {(serviceType === 'General' || !serviceType) && (
                  <>
                    <option value="Training">Corporate Training</option>
                    <option value="Recruitment">Recruitment Services</option>
                    <option value="Software">Software Development</option>
                    <option value="IT Services">IT Services</option>
                    <option value="Other">Other</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#9CA3AF] mb-1">Additional Details</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3 bg-[#0B0F1A] border border-[#00E5FF]/20 rounded-lg text-white placeholder-[#6B7280] focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all resize-none"
                placeholder="Tell us more about your requirements..."
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#00E5FF] hover:bg-[#1DE9B6] text-[#0B0F1A] font-semibold py-4 rounded-lg shadow-lg shadow-[#00E5FF]/40 hover:shadow-[#00E5FF]/60 transition-all duration-300 flex items-center justify-center gap-2"
            >
              {loading ? (
                <span className="animate-spin w-5 h-5 border-2 border-[#0B0F1A] border-t-transparent rounded-full"></span>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ConsultationModal;
